import {
  defaultDataIdFromObject,
  split,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Operation,
} from "apollo-boost";
import { BatchHttpLink } from "apollo-link-batch-http";
import { onError, ErrorHandler } from "apollo-link-error";
import { createHttpLink, UriFunction } from "apollo-link-http";
import { NetworkErrorLink } from "apollo-link-network-error";
import { createPersistedQueryLink } from "apollo-link-persisted-queries";
import { filter, get, includes, isEmpty, isUndefined, omitBy } from "lodash";
import * as Cookies from "js-cookie";

import schema from "../clientSchema";
import {
  notifyServerError,
  ServerErrorType,
} from "../components/common/ServerErrorModal";
import { LISTING_STATUS_CODE } from "../components/common/const";
import { PROPERTY_LISTINGS_QUERY_NAME } from "../components/common/queries";

const isDevelopment = process.env.NODE_ENV === "development";
const TRIM_FLAG_COOKIE = "ct413";

type GraphQLErrorType = {
  statusCode: number;
  location: string;
  errors: string[];
};

type NetworkErrorType = {
  name: string;
  statusCode: number;
  response: {};
  result: {
    errors: {
      errors: string[];
      location: string;
      statusCode: number;
    }[];
  };
};

export const getUMEndPoint = () =>
  process.env.REACT_APP_UM_ENDPOINT || "https://www.iproperty.com.my/users";

export const errorHandler: ErrorHandler = ({ graphQLErrors, operation }) => {
  if (isEmpty(graphQLErrors)) {
    return;
  }

  const expiryCustomer = (graphQLErrors || []).some(
    (error: GraphQLErrorType) =>
      error.statusCode === 403 &&
      error?.errors?.some((error) => error === "EXPIRY_CUSTOMER")
  );
  if (expiryCustomer) {
    window.location.href = getUMEndPoint() + "/expiry";
    return;
  }

  const authenticationError = filter(
    graphQLErrors,
    (error: GraphQLErrorType) =>
      error.statusCode === 401 &&
      includes(error.errors, "UNAUTHENTICATED_HTTP_RESPONSES") &&
      !!location
  );
  if (!isEmpty(authenticationError) && authenticationError[0]) {
    window.location.href = get(authenticationError, "[0].location");
    return;
  }

  const noPermissionError = filter(
    graphQLErrors,
    (error: GraphQLErrorType) => error.statusCode === 403
  );
  if (!isEmpty(noPermissionError)) {
    window.location.href = getUMEndPoint() + "/no-permission";
    return;
  }

  if (!operation.getContext().ignoreError) {
    notifyServerError(graphQLErrors as ServerErrorType[]);
  }
};

const networkErrorHandler = ({
  networkError,
  operation,
}: {
  networkError: NetworkErrorType;
  operation: Operation;
}) => {
  if (isEmpty(networkError)) {
    return Promise;
  }

  // If RASOR returns 413 (Payload Too Large), trim large cookies and refresh.
  // If we've already trimmed once (flag cookie present), don't redirect again to avoid loops.
  if (networkError.statusCode === 413) {
    const alreadyTrimmed = Cookies.get(TRIM_FLAG_COOKIE) === "1";

    if (alreadyTrimmed) {
      // Do not redirect/reload again to avoid loops
      return Promise;
    }

    // Mark that we already trimmed once (expire after 5 minutes) and then perform trimming + reload
    Cookies.set(TRIM_FLAG_COOKIE, "1", {
      path: "/",
      // js-cookie: Date object sets absolute expiry time
      expires: new Date(Date.now() + 5 * 60 * 1000),
    });
    trimLargeCookies();
    return Promise;
  }

  // skip notify server error as 401 and 403 are handled by errorHandler
  if ([401, 403].includes(networkError.statusCode)) {
    return Promise;
  }

  // allow to pass timeout error for certain endpoint
  if (operation.operationName !== "marketComparison") {
    const error: ServerErrorType = {
      statusCode: networkError.statusCode || 500,
    };

    if (!isEmpty(networkError.name)) {
      error.message = networkError.name;
    }

    notifyServerError(error);
  }

  return Promise;
};

// Clear all cookies except whitelisted ones, then refresh the page
export const trimLargeCookies = () => {
  try {
    const COOKIE_WHITELIST = [
      "authExtra",
      "auth_idt",
      "auth_rt",
      "auth_at",
      "defaultLocale",
      "from",
      "commsID",
      "pgutid",
      "ajs_user_id",
      "ajs_anonymous_id",
      "x-impersonate-userid",
      "platform",
      "user_info_update_at",
      "x-lead-unread-count",
      TRIM_FLAG_COOKIE,
    ];

    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach((key) => {
      if (!COOKIE_WHITELIST.includes(key)) {
        // attempt to remove with default options
        Cookies.remove(key);
        // also try removing from root path
        Cookies.remove(key, { path: "/" });
      }
    });
  } catch (e) {
    // no-op; proceed to refresh regardless
  } finally {
    // refresh page to retry request without large cookies
    window.location.reload();
  }
};

const errorLink = onError(errorHandler);
const networkErrorLinkInstance = new NetworkErrorLink(networkErrorHandler);

const uri = process.env.REACT_APP_RASOR_ENDPOINT
  ? `${process.env.REACT_APP_RASOR_ENDPOINT}/graphql`
  : "/graphql";

export const uriFunction: UriFunction = (operation) => {
  const operationName = operation.operationName;
  const id = operation.variables.id;

  if (id) {
    return `${uri}/${operationName}/${id}`;
  }

  return `${uri}/${operationName}`;
};

const httpLink = createHttpLink({
  uri: uriFunction,
  credentials: "include",
  headers: {},
});

const batchHttpLink = new BatchHttpLink({
  uri,
  batchMax: 10,
  credentials: "include",
  headers: {
    "x-batch": "true",
  },
});

export const prefetchingLink = new ApolloLink((operation, forward) => {
  const { variables, extensions, operationName } = operation;
  if (operationName === PROPERTY_LISTINGS_QUERY_NAME) {
    prefetchOtherStatusListings(operation);
    const currentPage = variables.page ? parseInt(variables.page, 10) : 1;
    const currentVariables = omitBy(variables, isUndefined);
    const exts = JSON.stringify(extensions);

    appendHeadPrefetchLink(
      buildQueryLink(
        JSON.stringify({ ...currentVariables, page: `${currentPage + 1}` }),
        exts,
        operation
      )
    );

    if (currentPage > 1) {
      appendHeadPrefetchLink(
        buildQueryLink(
          JSON.stringify({ ...currentVariables, page: `${currentPage - 1}` }),
          exts,
          operation
        )
      );
    }
  }

  if (isDevelopment) {
    const startTime = new Date().getTime();
    const queryLogger = forward(operation).map((data) => {
      const elapsed = new Date().getTime() - startTime;
      console.warn(`[METRICS][${operationName}]: ${elapsed} ms`);
      return data;
    });

    return queryLogger;
  }

  return forward ? forward(operation) : null;
});

const apolloLinkOptions = [
  networkErrorLinkInstance,
  errorLink,
  split(
    (operation) => operation.getContext().batch === true,
    batchHttpLink,
    persistable(prefetchingLink.concat(httpLink))
  ),
];

export const client = new ApolloClient({
  cache: new InMemoryCache({
    dataIdFromObject: (obj) => {
      switch (obj.__typename) {
        case "LocationLevel1":
        case "LocationLevel2":
        case "LocationLevel3":
        case "LocationLevel4":
        case "LocationLevel5":
          return obj.id
            ? `${obj.__typename}:${obj.id}`
            : `${obj.__typename}:${JSON.stringify(get(obj, "text"))}`;
        default:
          return defaultDataIdFromObject(obj);
      }
    },
    cacheRedirects: {
      Query: {
        listingsOfCache: (_, { ids }, { getCacheKey }) =>
          ids.map((id: string) => getCacheKey({ __typename: "Listing", id })),
        listingOfCache: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: "Listing", id }),
      },
    },
  }),
  typeDefs: schema.loc.source.body,
  link: ApolloLink.from(apolloLinkOptions),
  connectToDevTools: true,
});

function persistable(link: ApolloLink) {
  return createPersistedQueryLink({
    useGETForHashedQueries: true,
  }).concat(link);
}

const prefetchOtherStatusListings = (operation: Operation) => {
  const currentVariables = omitBy(operation.variables, isUndefined);
  const statusCodes = omitBy(
    LISTING_STATUS_CODE,
    (code) =>
      code === (operation.variables.statusCode || LISTING_STATUS_CODE.ONLINE) ||
      code === LISTING_STATUS_CODE.DELETED
  );
  // tslint:disable-next-line:forin
  for (const type in statusCodes) {
    const codeType = type as "DRAFT" | "ONLINE" | "OFFLINE" | "EXPIRED";
    const statusCodeQueryLink = buildQueryLink(
      JSON.stringify({
        ...currentVariables,
        statusCode: statusCodes[codeType],
        page: "1",
      }),
      JSON.stringify(operation.extensions),
      operation
    );
    appendHeadPrefetchLink(statusCodeQueryLink);
  }
};

const appendHeadPrefetchLink = (link: string) => {
  if (!document) {
    return;
  }
  const existingLink = document.querySelector(`link[href="${link}"]`);
  if (existingLink) {
    document.head.removeChild(existingLink);
  }
  const linkNode = document.createElement("link");
  linkNode.setAttribute("rel", "prefetch");
  linkNode.setAttribute("href", link);
  linkNode.setAttribute("as", "fetch");
  document.head.appendChild(linkNode);
};

const buildQueryLink = (
  variables: string,
  extensions: string,
  operation: Operation
) => {
  return `${uriFunction(operation)}?operationName=${
    operation.operationName
  }&variables=${encodeURIComponent(variables)}&extensions=${encodeURIComponent(
    extensions
  )}`;
};
